import _ from 'underscore'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import EditRoleFeature from 'components/UserInfoMenu/FeaturesManager/EditRoleFeature'
import Modal from 'components/Modal'
import Spinner from 'components/Spinner'
import Table from 'components/Table'
import Checkbox from 'components/Checkbox'

import Environment from 'helpers/environment'

import useRoleFeatures from 'hooks/useRoleFeatures'

import Form from 'pages/shared/Form'

import { RoleFeature } from 'types/RoleFeature'

import Api from 'services/api'

import { color } from 'styles/colors'

type Props = {
  closeCallback: (reloadPage) => void
}

const RoleFeaturesModal = ({ closeCallback }: Props) => {
  const [isSaving, setIsSaving] = useState(false)
  const { roleFeatures, isRoleFeaturesLoaded } = useRoleFeatures()
  const [updatedRoleFeatures, setUpdatedRoleFeatures] = useState<RoleFeature[]>([])
  const [productionConfirmation, setProductionConfirmation] = useState(false)

  useEffect(() => {
    setUpdatedRoleFeatures(roleFeatures || [])
  }, [roleFeatures])

  const onUpdateRoleFeatureCallback = useCallback((oldRoleFeature: RoleFeature, updatedRoleFeature: RoleFeature) => {
    setUpdatedRoleFeatures([..._.without(updatedRoleFeatures, oldRoleFeature), updatedRoleFeature])
  }, [updatedRoleFeatures])

  const onClickSave = useCallback(async () => {
    setIsSaving(true)
    await Api.FeatureCodes.updateRoleFeatures({ roleFeatures: updatedRoleFeatures })
      .then(() => {
        closeCallback(false)
      }).catch(() => setIsSaving(false))
  }, [closeCallback, updatedRoleFeatures])

  const canMakeChanges = useCallback(() => {
    return !Environment.isProduction() || productionConfirmation
  }, [productionConfirmation])

  if (!isRoleFeaturesLoaded) {
    return <Spinner variant="page" />
  }

  return (
    <Modal variant="sm" isDisplayed closeCallback={() => closeCallback(false)} title={'Beta Features'} >
      <S.Form title="" isSaving={isSaving} saveDisabled={!canMakeChanges()} onSaveCallback={onClickSave} onCancelCallback={() => closeCallback(false)} noBorder>
        {
          Environment.isProduction() &&
            <S.ConfirmProductionChangesContainer onClick={() => setProductionConfirmation(!productionConfirmation)}>
              <S.ComfirmProductionChanges
                label={'WARNING: This is PRODUCTION and these changes will affect all users. Check this box to comfirm you want to make changes.'}
                checked={productionConfirmation}
                onChange={() => setProductionConfirmation(productionConfirmation)}
              />
            </S.ConfirmProductionChangesContainer>
        }
        <Table
          items={roleFeatures || []}
          noDataMessage={{
            title: 'No Feature codes Available',
            description: ''
          }}
        >
          <Table.Header>
            <Table.HeaderCell title="Feature Code" />
            <S.RowsHeaderCell title="Enable for Roles" />
          </Table.Header>

          {_.sortBy(updatedRoleFeatures ?? [], 'featureCode').map((roleFeature) => (
            <EditRoleFeature
              key={roleFeature.featureCode}
              disabled={!canMakeChanges()}
              roleFeature={roleFeature}
              onUpdateRoleFeatureCallback={onUpdateRoleFeatureCallback}
            />
          ))}
        </Table>
      </S.Form>
    </Modal>
  )
}

export default RoleFeaturesModal

const S = {
  RowsHeaderCell: styled(Table.HeaderCell)`
    min-width: 300px;
  `,
  Form: styled(Form)`
    & fieldset{
      padding: 0;
    }
  `,
  ConfirmProductionChangesContainer: styled.div`
    background-color: ${color('danger')};
    margin-bottom: 20px;
    margin-top: -15px;
    padding: 5px 10px;
    cursor: pointer;
  `,
  ComfirmProductionChanges: styled(Checkbox)`
    margin-bottom: 0;

    label {
      font-weight: bold;
      color: ${color('dangerText')};
    }
  `
}
